// src/components/NetworkAdvisor.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './NetworkAdvisor.css';

const NetworkAdvisor = ({ onClose }) => {
  const [answers, setAnswers] = useState({
    users: '',
    locations: '',
    security: 'Moderate'
  });
  const [recommendation, setRecommendation] = useState(null);
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);

  // Fetch rules from WordPress
  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await fetch('https://cms.vxpath.com/wp-json/wp/v2/network_rule?per_page=20');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const wpRules = await response.json();

        const formattedRules = wpRules.map(item => {
          // Parse security levels — ACF returns string or array
          const securityLevels = Array.isArray(item.acf?.security_levels)
            ? item.acf.security_levels
            : (item.acf?.security_levels ? [item.acf.security_levels] : []);

          return {
            condition: {
              maxUsers: parseInt(item.acf?.max_users, 10) || 9999,
              maxLocations: parseInt(item.acf?.max_locations, 10) || 9999,
              securityLevels: securityLevels
            },
            recommendation: {
              title: item.acf?.rec_title || 'Custom Enterprise Solution',
              summary: item.acf?.rec_summary || 'Your needs require a tailored architecture.',
              firewall: item.acf?.rec_firewall || '—',
              switch: item.acf?.rec_switch || '—',
              wifi: item.acf?.rec_wifi || '—',
              camera: item.acf?.rec_camera || '—'
            }
          };
        });

        // Optional: sort by priority (if you add an ACF "order" field later)
        setRules(formattedRules);
      } catch (error) {
        console.error('Failed to load network rules from WordPress:', error);
        // Fallback: you could import local rules here if needed
        setRules([]);
      } finally {
        setLoadingRules(false);
      }
    };

    fetchRules();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnswers(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const userNum = parseInt(answers.users, 10);
    const locNum = parseInt(answers.locations, 10);
    const securityLevel = answers.security;

    // 🔥 Use `rules` (from WordPress) instead of `networkSolutions`
    const matchedSolution = rules.find(rule => {
      return (
        userNum <= rule.condition.maxUsers &&
        locNum <= rule.condition.maxLocations &&
        rule.condition.securityLevels.includes(securityLevel)
      );
    });

    setRecommendation(matchedSolution?.recommendation || {
      title: "Custom Enterprise Design",
      summary: "Your requirements are unique. Let's build a tailored solution together.",
      firewall: "Custom (Cisco, Palo Alto, or Fortinet)",
      switch: "Custom Enterprise Switch",
      wifi: "Custom Wi-Fi 6E/7 Design",
      camera: "Scalable Video Surveillance"
    });
  };

  // Show loading state if rules haven't loaded yet AND form hasn't been submitted
  if (loadingRules && !recommendation) {
    return (
      <div className="network-advisor">
        <p>Loading network advisor...</p>
      </div>
    );
  }

  return (
    <div className="network-advisor">
      {!recommendation ? (
        <>
          <h2>Network Design Questionnaire</h2>
          <p className="advisor-subtitle">Get a tailored solution in 60 seconds</p>
          
          <form onSubmit={handleSubmit} className="advisor-form">
            <div className="form-group">
              <label>Total Users</label>
              <select 
                name="users" 
                value={answers.users} 
                onChange={handleChange} 
                required
              >
                <option value="">Select user range</option>
                <option value="10">1–10 users</option>
                <option value="50">11–50 users</option>
                <option value="200">51–200 users</option>
                <option value="1000">200+ users</option>
              </select>
            </div>

            <div className="form-group">
              <label>Number of Locations</label>
              <select 
                name="locations" 
                value={answers.locations} 
                onChange={handleChange} 
                required
              >
                <option value="">Select location count</option>
                <option value="1">1 location</option>
                <option value="5">2–5 locations</option>
                <option value="10">6–10 locations</option>
                <option value="100">10+ locations</option>
              </select>
            </div>

            <div className="form-group">
              <label>Security Level</label>
              <div className="radio-group">
                {['Basic', 'Moderate', 'High', 'Enterprise'].map(level => (
                  <label key={level} className="radio-option">
                    <input
                      type="radio"
                      name="security"
                      value={level}
                      checked={answers.security === level}
                      onChange={handleChange}
                    />
                    <span>{level}</span>
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" className="btn-primary">Get Recommendation</button>
          </form>
        </>
      ) : (
        <div className="recommendation-result">
          <h3>{recommendation.title}</h3>
          <p className="result-summary">{recommendation.summary}</p>
          
          <div className="solution-details">
            <div className="solution-item">
              <strong>Firewall:</strong> {recommendation.firewall}
            </div>
            <div className="solution-item">
              <strong>Switch:</strong> {recommendation.switch}
            </div>
            <div className="solution-item">
              <strong>Wi-Fi:</strong> {recommendation.wifi}
            </div>
            <div className="solution-item">
              <strong>Cameras:</strong> {recommendation.camera}
            </div>
          </div>

          <div className="result-cta">
            <Link to="/contact" className="btn-primary" onClick={onClose}>
              Schedule Free Consultation
            </Link>
            <Link to="/solutions/meraki-vs-unifi" className="btn-text">
              {/* See Full Comparison → */}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkAdvisor;